import React from 'react';
import styles from './Notifications.module.scss';
import SourceNotificationSettings from 'js/components/elements/notifications/SourceNotificationSettings';
import {
  deleteResourceNotificationSetting,
  loadNotificationsChannels,
  loadNotificationsSettings,
  loadNotificationsTypes,
  updateNotificationManager,
} from 'js/actions';
import { useDispatch, useSelector } from 'react-redux';
import has from 'lodash/has';
import map from 'lodash/map';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  CREATE_NOTIFICATIONS_SETTINGS_REQUEST,
  DELETE_NOTIFICATIONS_SETTINGS_REQUEST,
  DELETE_RESOURCE_NOTIFICATION_REQUEST,
  GET_NOTIFICATIONS_CHANNEL_REQUEST,
  GET_NOTIFICATIONS_SETTINGS_REQUEST,
  GET_NOTIFICATIONS_TYPES_REQUEST,
  GET_RESOURCE_NOTIFICATIONS_REQUEST,
  UPDATE_NOTIFICATIONS_SETTINGS_REQUEST,
} from 'js/constants/actionTypes';
import { SOURCE } from 'js/constants/notificationsTypes';
import { Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { NotificationSettings } from 'new/components/monitor/NotificationSettings';
import useAppDispatch from 'js/hooks/useAppDispatch';

interface SettingsProps {
  resourceId: string;
  resourceType: string;
}

export const Settings = (props: SettingsProps) => {
  const { resourceType: type, resourceId: id } = props;
  const dispatch = useAppDispatch();
  const {
    notification_types: notificationTypes,
    notification_channels_enum_map: notificationChannelsEnumMap,
    notificationSettingsByResourceType,
  } = useSelector((state: any) => state.notificationsManager || {});

  const isLoadingSettings = useIsLoading(
    GET_NOTIFICATIONS_TYPES_REQUEST,
    GET_NOTIFICATIONS_CHANNEL_REQUEST,
    GET_NOTIFICATIONS_SETTINGS_REQUEST,
  );

  const isSavingSettings = useIsLoading(
    UPDATE_NOTIFICATIONS_SETTINGS_REQUEST,
    CREATE_NOTIFICATIONS_SETTINGS_REQUEST,
    DELETE_NOTIFICATIONS_SETTINGS_REQUEST,
  );

  const isLoadingNotifications = useIsLoading(
    GET_RESOURCE_NOTIFICATIONS_REQUEST,
    DELETE_RESOURCE_NOTIFICATION_REQUEST,
  );

  const notificationSettings = useSelector((state: any) => state.entities?.notificationSettings);

  const sortedIds = has(notificationSettingsByResourceType, [type, id])
    ? notificationSettingsByResourceType[type][id]
    : [];

  const sortedNotificationSettings = map(sortedIds, (id) => notificationSettings[id]);

  const loadNotificationsTypesCallback = () => {
    return dispatch(loadNotificationsTypes());
  };
  const loadNotificationsChannelsCallback = () => {
    return dispatch(loadNotificationsChannels());
  };
  const loadNotificationsSettingsCallback = (...args) => {
    return dispatch(loadNotificationsSettings(...args));
  };
  const updateNotificationManagerCallback = (...args) => {
    return dispatch(updateNotificationManager(...args));
  };
  const deleteResourceNotificationSettingCallback = (...args) => {
    return dispatch(deleteResourceNotificationSetting(...args));
  };

  if (props.resourceType !== SOURCE) {
    return (
      <Typography>
        Go to{' '}
        <Link to={NotificationSettings.screenConfig.path as string}>Notification Settings</Link> to
        set Account level notifications on all Resources.
      </Typography>
    );
  }

  return (
    <div className={styles.settingsContainer}>
      <SourceNotificationSettings
        description={
          <Typography>
            Set Data Volume Alerts for this source. You can also go to{' '}
            <Link to={NotificationSettings.screenConfig.path as string}>Notification Settings</Link>{' '}
            to set Account level notifications on all Resources.
          </Typography>
        }
        isLoadingSettings={isLoadingSettings}
        isSavingSettings={isSavingSettings}
        isLoadingNotifications={isLoadingNotifications}
        notificationSettings={sortedNotificationSettings}
        notificationTypes={notificationTypes}
        notificationChannelsEnumMap={notificationChannelsEnumMap}
        notificationSettingsByResourceType={notificationSettingsByResourceType}
        loadNotificationsTypes={loadNotificationsTypesCallback}
        loadNotificationsChannels={loadNotificationsChannelsCallback}
        loadNotificationsSettings={loadNotificationsSettingsCallback}
        updateNotificationManager={updateNotificationManagerCallback}
        deleteResourceNotificationSetting={deleteResourceNotificationSettingCallback}
        type={props.resourceType}
        id={props.resourceId}
      />
    </div>
  );
};
